import { NodeDefinition } from 'Automations/nodeDefinitions/types'
import { ReviewEditorNode } from '../review.editorNode'
import { ReviewIcon } from '../review.icon'
import { ReviewQuoteConfigForm } from './reviewQuote.configForm'
import { ReviewType } from '..'
import {
  createSimpleTask,
  createSwitchTask,
  getTaskReferenceName,
  wrapVariables
} from 'Automations/nodeDefinitions/conductor'
import {
  getClosestMatches,
  semanticTypes
} from 'Automations/nodeDefinitions/semanticTypes'
import {
  approversRequired,
  handlesConnected,
  reviewTypeRequired
} from '../review.validators'
import { ReviewQuoteExecutionSummary } from './ReviewQuote.executionSummary'
import { quoteReviewManualTask } from './manualTask'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

export type Config = {
  name: string
  reviewType: ReviewType
  approverUserIds: string[]
}

type Inputs = 'quote' | 'user' | 'customer' | 'requestorComment'

type TaskName = 'manualReview' | 'reviewOutcome'

const taskReferenceBuilder = getTaskReferenceName<TaskName>('REVIEW_QUOTE')

export const reviewQuoteNodeDefinition: NodeDefinition<Config, Inputs> = {
  name: 'Review',
  description: 'Quote',
  editorNode: ReviewEditorNode,
  configForm: ReviewQuoteConfigForm,
  icon: ReviewIcon,
  defaultConfig: {
    name: '',
    reviewType: 'QUOTE',
    approverUserIds: []
  },
  validation: {
    node: { ERROR: [], WARN: [] },
    config: {
      name: { ERROR: [], WARN: [] },
      reviewType: {
        ERROR: [reviewTypeRequired],
        WARN: []
      },
      approverUserIds: {
        ERROR: [approversRequired],
        WARN: []
      }
    },
    handles: { ERROR: [handlesConnected], WARN: [] }
  },
  handleMapping: () => ({
    APPROVED: 0,
    REJECTED: 1
  }),
  inputMapping: ({ upstreamNodes }) => {
    return getClosestMatches<Inputs>(upstreamNodes)({
      quote: semanticTypes.sequence.quote.quote,
      user: semanticTypes.sequence.user,
      customer: semanticTypes.sequence.customer.customer,
      requestorComment: semanticTypes.sequence.quoteApproval.requestorComment
    })
  },
  taskTransformer: ({ nodeId, inputMapping, branches, config }) => {
    const taskReferenceName = taskReferenceBuilder(nodeId)

    return [
      createSimpleTask({
        name: 'manual_task',
        taskReferenceName: taskReferenceName('manualReview'),
        inputParameters: {
          taskExecutorConfiguration: {
            executor: TASK_EXECUTORS.reviewQuote,
            version: 1
          },
          data: {
            ...wrapVariables({
              quote: inputMapping.quote,
              customer: inputMapping.customer,
              requestor: inputMapping.user,
              comment: inputMapping.requestorComment
            }),
            assignmentRules: {
              type: 'users',
              users: config.approverUserIds
            }
          }
        }
      }),
      createSwitchTask({
        name: 'approval_decision',
        taskReferenceName: taskReferenceName('reviewOutcome'),
        inputParameters: wrapVariables({
          outcome: `${taskReferenceName('manualReview')}.output.outcome`
        }),
        evaluatorType: 'value-param',
        expression: 'outcome',
        decisionCases: {
          APPROVED: branches[0],
          REJECTED: branches[1]
        },
        defaultCase: []
      })
    ]
  },
  executionSummary: ReviewQuoteExecutionSummary,
  manualTask: {
    type: 'QUOTE_APPROVAL',
    ...quoteReviewManualTask
  }
}
